import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ROLES, ROLE_LABELS } from '@recica/shared/permissions';
import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { usersApi } from '../services/usersApi.js';

const EMPTY = { email: '', password: '', firstName: '', lastName: '', roles: [] };

export function UserCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(EMPTY); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function toggleRole(role) {
    setForm((f) => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role] }));
  }

  const create = useMutation({
    mutationFn: (body) => usersApi.create(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      roles: form.roles,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('settings.users.create.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={create.isPending}>{create.isPending ? `${t('common.create')}…` : t('common.create')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.users.firstName')}</span>
            <input className="input" value={form.firstName} onChange={set('firstName')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.users.lastName')}</span>
            <input className="input" value={form.lastName} onChange={set('lastName')} required />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('settings.users.email')}</span>
          <input className="input" type="email" value={form.email} onChange={set('email')} autoComplete="off" required />
        </label>
        <label className="field">
          <span className="field-label">{t('settings.users.password')}</span>
          <input className="input" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required />
          <span className="muted text-sm">{t('settings.users.passwordHint')}</span>
        </label>
        <div className="field">
          <span className="field-label">{t('settings.users.roles')}</span>
          <div className="user-admin-roles">
            {ROLES.map((role) => (
              <label key={role} className="user-admin-role">
                <input type="checkbox" checked={form.roles.includes(role)} onChange={() => toggleRole(role)} />
                <span>{t(ROLE_LABELS[role])}</span>
              </label>
            ))}
          </div>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
